import {Content} from '@hconnect/uikit'
import {PageHeader} from '@hm/fundamental'
import AddIcon from '@mui/icons-material/Add'
import {Button} from '@mui/material'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {useHistory, useParams} from 'react-router'

import {useCountriesConfiguration} from '../../common/hooks/useCountriesConfiguration'
import {CompanyFormData} from '../../components/Company/CompanyForm.types'
import {ManageCompanySubscriptions} from '../../components/Company/manageCompanySubscriptions/ManageCompanySubscriptions'
import {CompanyDetailView} from '../../components/Company/ManageCompanyView'
import {ManageCompanyMembers} from '../../components/Company/manageMemberAssignment/ManageCompanyMembers'
import {useFetchCompanyDetail} from '../../hooks/useCompany'
import {useTranslation} from '../../hooks/useTranslation'

import {useStyles} from './ManageCompany.styles'

export const ManageCompany = () => {
  const {t} = useTranslation()
  const history = useHistory()
  const {classes} = useStyles()
  const {companyId} = useParams<{companyId: string}>()
  const {enqueueSnackbar} = useSnackbar()
  const [pageTitle, setPageTitle] = useState<string>(t('company.manage.title'))
  const {data: availableCountries, error: errorAvailableCountries} = useCountriesConfiguration()
  const {data: company, isSuccess, isError} = useFetchCompanyDetail(companyId)
  const formMethods = useForm<CompanyFormData>({
    defaultValues: {
      id: '',
      country: '',
      availableCountries: [],
      website: '',
      industry: '',
      name: '',
      webhookNotificationEmails: []
    },
    mode: 'all',
    reValidateMode: 'onChange'
  })

  if (isError) {
    enqueueSnackbar(t('company.manage.cannotLoadCompanyDetails'), {
      anchorOrigin: {vertical: 'top', horizontal: 'center'},
      variant: 'error'
    })
  }

  useEffect(() => {
    if (company) {
      setPageTitle(`${t('company.manage.title')} | ${company.name}`)

      formMethods.reset({
        id: company.id,
        country: company.country,
        website: company.website,
        industry: company.industry,
        availableCountries: availableCountries,
        name: company.name,
        webhookNotificationEmails: company.webhookNotificationEmails ?? []
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, availableCountries])

  if (errorAvailableCountries) {
    enqueueSnackbar(t('company.manage.cannotLoadAvailableCountries'), {
      anchorOrigin: {vertical: 'top', horizontal: 'center'},
      variant: 'error'
    })
  }

  return (
    <Content data-test-id="page-company-detail-content">
      <div className="[&_*]:!text-white">
        <PageHeader
          data-cy="page-company-detail-page"
          className="pl-0 pr-0 md:pl-0 md:pr-0"
          backToPageLabel={t('company.manage.goBack')}
          backToPageUrlOnClick={() => history.push('/companies')}
          aside={
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                data-test-id="create-user-button"
                onClick={() => history.push('/createUser')}
                className={classes.addUserButton}
              >
                {t('company.manage.createUserButton')}
              </Button>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                data-test-id="create-company-button"
                onClick={() => history.push('/companies/create')}
                className={classes.addCompanyButton}
              >
                {t('company.manage.createCompanyButton')}
              </Button>
            </>
          }
        >
          {pageTitle}
        </PageHeader>
      </div>

      {isSuccess && (
        <FormProvider {...formMethods}>
          <CompanyDetailView />

          <ManageCompanySubscriptions companyId={companyId} />

          <ManageCompanyMembers members={company?.companyMembers ?? []} companyId={companyId} />
        </FormProvider>
      )}
    </Content>
  )
}
